import { Button, Card, Form, Grid, Input, Message, Select, Space, Table } from "@arco-design/web-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InvAdjustmentOrderAPI, WarehouseAPI } from "@/apis/material";
import { InputNumber } from "@/components/Numeric";
import { Currency } from "@/components/Numeric";
import WarehouseSelect from "@/components/WarehouseSelect";
import AccountSelect from "@/components/AccountSelect";
import { ItemSelect } from "@/components/ItemSelect";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import { AccountCategory } from "@/enums/accounting";
import { InvAdjustmentOrderType } from "@/enums/material";
import Decimal from "decimal.js";
import _ from "lodash";

const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;
const TextArea = Input.TextArea;

const counterAccountCategories = [AccountCategory.Equity, AccountCategory.Liability, AccountCategory.Expenses];

const newItemRow = () => ({
    key: _.uniqueId("row_"),
    itemId: undefined,
    itemName: "",
    description: "",
    locationId: undefined,
    locationName: "",
    lot: "",
    qty: null,
    costPerUnit: null,
    totalValue: null,
});

export const InvNewInventoryAdd = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [locations, setLocations] = useState([]);
    const [rows, setRows] = useState([newItemRow()]);

    const totalValue = _.reduce(rows, (sum, row) => {
        return new Decimal(sum).plus(row.totalValue ?? 0).toNumber();
    }, 0);

    const onWarehouseChange = useCallback(async (val) => {
        form.setFieldValue("warehouseId", val);
        if (val) {
            const values = await WarehouseAPI.locations(val);
            setLocations(_.map(_.orderBy(values.records, ["name"], ["asc"]), item => ({
                value: item.id,
                label: item.name,
            })));
        } else {
            setLocations([]);
        }
    }, [form]);

    const onItemSelect = useCallback((index, item) => {
        const itemId = item?.value;
        setRows(prev => {
            const newRows = [...prev];
            newRows[index] = {
                ...newRows[index],
                itemId,
                itemName: item?.itemName || "",
                description: item?.description || "",
            };
            return newRows;
        });
    }, []);

    const onLocationChange = useCallback((index, val) => {
        const option = _.find(locations, { value: val });
        setRows(prev => {
            const newRows = [...prev];
            newRows[index] = { ...newRows[index], locationId: val, locationName: option?.label || "" };
            return newRows;
        });
    }, [locations]);

    const onFieldChange = useCallback((index, field, val) => {
        setRows(prev => {
            const newRows = [...prev];
            const row = newRows[index];
            newRows[index] = { ...row, [field]: val };

            if (field === "qty" || field === "costPerUnit") {
                const qty = field === "qty" ? val : row.qty;
                const costPerUnit = field === "costPerUnit" ? val : row.costPerUnit;
                if (qty != null && costPerUnit != null) {
                    newRows[index].totalValue = new Decimal(qty).times(costPerUnit).toNumber();
                }
            }

            return newRows;
        });
    }, []);

    const addRow = useCallback(() => {
        setRows(prev => [...prev, newItemRow()]);
    }, []);

    const removeRow = useCallback((index) => {
        setRows(prev => {
            const newRows = [...prev];
            newRows.splice(index, 1);
            if (newRows.length === 0) newRows.push(newItemRow());
            return newRows;
        });
    }, []);

    const onSubmit = async () => {
        try {
            const values = await form.validate();

            for (let i = 0; i < rows.length; i++) {
                if (!rows[i].itemId) {
                    Message.error(`Row ${i + 1}: Item is required`);
                    return;
                }
                if (rows[i].qty == null || rows[i].qty <= 0) {
                    Message.error(`Row ${i + 1}: Qty must be greater than 0`);
                    return;
                }
                if (rows[i].costPerUnit == null) {
                    Message.error(`Row ${i + 1}: Cost/Unit is required`);
                    return;
                }
            }

            const params = {
                orderType: InvAdjustmentOrderType.NewInventory,
                accountId: values.accountId,
                warehouseId: values.warehouseId,
                notes: values.notes,
                items: _.map(rows, row => ({
                    itemId: row.itemId,
                    itemName: row.itemName,
                    itemDescription: row.description,
                    locationId: row.locationId,
                    locationName: row.locationName,
                    lot: row.lot || null,
                    qty: row.qty,
                    costPerUnit: row.costPerUnit,
                    totalValue: row.totalValue,
                })),
            };

            setLoading(true);
            await InvAdjustmentOrderAPI.add(params);
            Message.success("New inventory created successfully");
            navigate("/workspace/material/inventory_adjustment");
        } catch (error) {
            if (error.name !== "ValidationError") {
                Message.error(error.message || "Failed to create new inventory");
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: (<div style={{ lineHeight: 1.5 }}><div style={{ fontSize: 14, fontWeight: 600 }}>Item Name</div><div style={{ fontSize: 12, color: '#86909c' }}>Description</div></div>), dataIndex: "itemId", width: 280,
            render: (col, record, index) => (
                <ItemSelect
                    value={record.itemId}
                    onSelectItem={(item) => onItemSelect(index, item)}
                    style={{ width: "100%" }} />
            ),
        },
        {
            title: "Location", dataIndex: "locationId", width: 160,
            render: (col, record, index) => (
                <Select showSearch allowClear placeholder="Select Location"
                    value={record.locationId}
                    onChange={(val) => onLocationChange(index, val)}
                    style={{ width: "100%" }}>
                    {_.map(locations, loc => (
                        <Select.Option key={loc.value} value={loc.value}>{loc.label}</Select.Option>
                    ))}
                </Select>
            ),
        },
        {
            title: "Lot#", dataIndex: "lot", width: 140,
            render: (col, record, index) => (
                <Input placeholder="Lot#" value={record.lot || ""}
                    onChange={(val) => onFieldChange(index, "lot", val)} />
            ),
        },
        {
            title: "Qty", dataIndex: "qty", width: 100,
            render: (col, record, index) => (
                <InputNumber placeholder="Qty" style={{ width: "100%" }} min={0}
                    value={record.qty}
                    onChange={(val) => onFieldChange(index, "qty", val)} />
            ),
        },
        {
            title: "Cost/Unit", dataIndex: "costPerUnit", width: 120,
            render: (col, record, index) => (
                <InputNumber placeholder="Cost" style={{ width: "100%" }} min={0}
                    value={record.costPerUnit}
                    onChange={(val) => onFieldChange(index, "costPerUnit", val)} />
            ),
        },
        {
            title: "Total Value", dataIndex: "totalValue", width: 120, align: "right",
            render: (col, record) => <span>{record.totalValue != null ? <Currency value={record.totalValue} /> : ""}</span>,
        },
        {
            title: "", width: 50, align: "center",
            render: (col, record, index) => (
                <Button type="text" icon={<IconDelete />} status="danger" onClick={() => removeRow(index)} />
            ),
        },
    ];

    return (
        <Card title="Add New Inventory" extra={
            <Space>
                <Button type="primary" onClick={onSubmit} loading={loading}>Submit</Button>
                <Button onClick={() => navigate("/workspace/material/inventory_adjustment")}>Cancel</Button>
            </Space>
        }>
            <Form form={form} colon labelCol={{ span: 8 }} wrapperCol={{ span: 15 }}>
                <Row gutter={24}>
                    <Col span={10}>
                        <FormItem field="accountId" label="Counter Account" required rules={[{ required: true, message: "Counter Account is required" }]}>
                            <AccountSelect category={counterAccountCategories} showSearch style={{ width: "100%" }} />
                        </FormItem>
                    </Col>
                    <Col span={8}>
                        <FormItem field="warehouseId" label="Warehouse" required rules={[{ required: true, message: "Warehouse is required" }]}>
                            <WarehouseSelect includeUnavailable placeholder="Select Warehouse" onChange={onWarehouseChange} />
                        </FormItem>
                    </Col>
                </Row>
                <FormItem field="notes" label="Notes" required rules={[{ required: true, message: "Notes is required" }]}
                    labelCol={{ span: 2 }} wrapperCol={{ span: 21 }}>
                    <TextArea placeholder="Notes" />
                </FormItem>
            </Form>

            <Table rowKey="key"
                columns={columns}
                data={rows}
                pagination={false}
                footer={() => (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Button type="outline" size="small" shape="round" status="success"
                            icon={<IconPlus />} onClick={addRow}>
                            Add Row
                        </Button>
                        <span>Total: <Currency value={totalValue} /></span>
                    </div>
                )}
            />
        </Card>
    );
};
